import { type CheckoutSummaryResponse } from './aliases';

import { type DetailedBagSummary } from '@/modules/bag/aliases';
import { type InlineBanners } from '@/modules/banners/aliases';
import { type CurrencyCode } from '@/modules/currencies/constants';
import {
  type PictureFormat,
  type PictureFormatV2,
  type ThumbnailFormat,
} from '@/modules/product/types';

export type CheckoutProduct = {
  id: number;
  slug: string;
  countryCode: string;
  price: {
    priceAmount?: string;
    nationalShippingCost?: string;
    internationalShippingCost?: string;
    discountedPriceAmount?: string;
    currencyName: CurrencyCode;
  };
  seller: {
    id: number;
    username: string;
    pictureUrl?: string;
    initials: string;
    verified?: boolean;
    firstName?: string;
    lastName?: string;
    picture?: PictureFormat[] | ThumbnailFormat[];
    reviewsTotal?: number;
    reviewsRating?: number;
    itemsSold?: number;
    lastSeen?: string;
    averageShippingDays?: number;
  };
  pictures: PictureFormat[][] | PictureFormatV2[][] | ThumbnailFormat[][] | [];
  size?: {
    id?: string;
    name?: string;
  };
  description: string;
  brandName?: string;
  productType?: string;
};

export enum CheckoutType {
  SingleProduct = 'singleProduct',
  Bag = 'bag',
}

export enum StockStatus {
  OnSale = 'ON_SALE',
  NotAvailable = 'NOT_AVAILABLE',
}

export enum CheckoutSummaryPaymentProvider {
  PayPal = 'PAYPAL',
  Stripe = 'STRIPE',
  Klarna = 'KLARNA',
}

export enum CheckoutSummaryDefaultPaymentMethodProvider {
  Stripe = 'STRIPE',
}

export enum CheckoutSummarySavedPaymentMethodProvider {
  Stripe = 'STRIPE',
}

export enum TaxType {
  UkImportVat = 'UK_IMPORT_VAT',
  UsMft = 'US_MFT',
  AuGst = 'AU_GST',
  CaMft = 'CA_MFT',
  EuImportVat = 'EU_IMPORT_VAT',
  NewZealandImportGst = 'NEW_ZEALAND_IMPORT_GST',
  NorwayImportLvgVat = 'NORWAY_IMPORT_LVG_VAT',
}

export enum PurchaseStatus {
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Repeat = 'REPEAT',
  Retry = 'RETRY',
}

export enum PaymentSourceProvider {
  Stripe = 'STRIPE',
  PayPal = 'PAYPAL',
}

export enum PaymentSourceType {
  PayPal = 'PAYPAL',
  CreditCard = 'CREDITCARD',
  ApplePay = 'APPLEPAY',
  GooglePay = 'GOOGLEPAY',
  Klarna = 'KLARNA',
}

export type StartPurchaseErrorResponse = {
  id: string;
  code: number;
  message: string;
};

export type InternalErrorResponse = {
  internalErrorCode: number;
  internalErrorMessage: string;
};

export enum ATPaymentProvider {
  PayPal = 'PAYPAL',
  Stripe = 'STRIPE',
}

export enum ATCheckoutSummaryViewBuyerPaymentMethod {
  PayPal = 'PAYPAL',
  Stripe = 'STRIPE',
  ApplePay = 'APPLEPAY',
  GooglePay = 'GOOGLEPAY',
  Klarna = 'KLARNA',
}

export enum ATStartPaymentActionPaymentType {
  PayPal = 'PAYPAL',
  CreditCard = 'CREDITCARD',
  ApplePay = 'APPLEPAY',
  GooglePay = 'GOOGLEPAY',
  Klarna = 'KLARNA',
}

export enum ATPurchaseConfirmationViewPaymentMethod {
  NewCard = 'STRIPE NEW CARD',
  SavedCard = 'STRIPE SAVED CARD',
  PayPal = 'PAYPAL',
  ApplePay = 'APPLEPAY',
  GooglePay = 'GOOGLEPAY',
  Klarna = 'KLARNA',
}

export type GaEcommerceItemData = {
  id: string;
  name: string;
  sku: string;
  price: number;
  quantity: number;
};

export type GaEcommerceTransactionData = {
  id: string;
  affiliation: string;
  value: number;
  shipping: number;
  currency: CurrencyCode;
};

// This is not strictly a domain concept but instead an app-level type
export enum PaymentTypeProvider {
  Stripe = 'Stripe',
  PayPal = 'PayPal',
}

// This is not strictly a domain concept but instead an app-level type
export enum PaymentTypeValue {
  PayPal = 'PayPal',
  NewCard = 'NewCard',
  ApplePay = 'ApplePay',
  GooglePay = 'GooglePay',
  Klarna = 'Klarna',
}

// This is not strictly a domain concept but instead an app-level type
export type PaymentType = {
  value: PaymentTypeValue | string;
  provider: PaymentTypeProvider;
  brand?: string;
  last4?: string;
  isSavedCard?: boolean;
  isWallet?: boolean;
  isNewCard?: boolean;
};

export type Ga4Item = {
  affiliation?: string;
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
};

export type Ga4Purchase = {
  currency: CurrencyCode;
  items: Ga4Item[];
  shipping: number;
  transaction_id: string;
  value: number;
};

export type RakutenItem = {
  quantity: number;
  unitPrice: number;
  sku: string;
  productName: string;
};

export type PinterestItem = {
  product_quantity: number;
  product_price: number;
  product_id: string;
  product_name: string;
};

export enum CountriesWithDropdownStates {
  US = 'US',
  IT = 'IT',
  AU = 'AU',
  GB = 'GB',
}

/**
 * The subset of the checkout summary that the order summary components
 * actually read. Typing those components against this (rather than the full
 * `CheckoutSummaryResponse`) lets both the authenticated summary and the mapped
 * guest bag summary satisfy the same prop without fabricating unused fields.
 */
export type OrderSummaryData = Pick<
  CheckoutSummaryResponse,
  | 'lines'
  | 'products_price'
  | 'shipping_price'
  | 'original_shipping_price'
  | 'total_price'
  | 'buyer_fee_amount'
  | 'tax_summary'
> & {
  total_price_components?: DetailedBagSummary['total_price_components'];
  /**
   * Not yet on `/v1/purchases/summary/` — PPX-1615 AC03 adds it. Optional so
   * the authenticated response satisfies this today and starts rendering
   * banners without a web change once the field arrives. The guest path
   * already supplies it from the bag's seller group.
   */
  banners?: InlineBanners;
};
